import React from 'react';
import { Container, Typography, Box, Paper, Divider } from '@mui/material';
import factImage from '../images/FACT_Model.jpeg';
import drawPlusImage from '../images/DRAW+_Model.webp';
import featureInjectionImage from '../images/Feature_Injection_Model.gif';

const Research: React.FC = () => {
  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom textAlign="center" sx={{ fontWeight: 'bold' }}>
          Our Research Areas
        </Typography>
        <Typography variant="body1" textAlign="center" sx={{ mb: 6 }}>
          Our lab is dedicated to pioneering research in various interdisciplinary fields, leveraging advanced computational methods to address complex challenges.
        </Typography>
      </Box>

      {/* Research Area 1: AI-based Drug Discovery */}
      <Box sx={{ my: 6 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          AI-based Drug Discovery
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          We apply cutting-edge artificial intelligence techniques to the entire drug discovery and development pipeline. Our research focuses on identifying novel biological targets, predicting drug-target interactions (DTI), designing new drug candidates (de novo drug design), and forecasting drug side effects and toxicity. By integrating deep learning, machine learning, and network analysis, we aim to significantly reduce the time and cost associated with bringing new therapies to patients.
        </Typography>
        
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 3 }}>
            Featured Publication
          </Typography>
          {/* FACT Publication */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'center', mb: 5 }}>
            <Box sx={{ flex: { md: '0 0 41.6667%' }, width: '100%' }}>
              <img src={factImage} alt="FACT Model Architecture" style={{ width: '100%', height: 'auto', borderRadius: '8px' }} />
            </Box>
            <Box sx={{ flex: { md: 1 } }}>
              <Typography variant="h6" component="h4" sx={{ fontWeight: 'bold' }}>
                FACT: Feature Aggregation and Convolution with Transformers for predicting drug classification code
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
                Gwang-Hyeon Yun, Jong-Hoon Park, and Young-Rae Cho
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, fontStyle: 'italic' }}>
                Bioinformatics (2025)
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* DRAW+ Publication */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'center' }}>
            <Box sx={{ flex: { md: '0 0 41.6667%' }, width: '100%' }}>
              <img src={drawPlusImage} alt="DRAW+ Model Architecture" style={{ width: '100%', height: 'auto', borderRadius: '8px' }} />
            </Box>
            <Box sx={{ flex: { md: 1 } }}>
              <Typography variant="h6" component="h4" sx={{ fontWeight: 'bold' }}>
                DRAW+: network-based computational drug repositioning with attention walking and noise filtering
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
                Jong-Hoon Park and Young-Rae Cho
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, fontStyle: 'italic' }}>
                Health Information Science and Systems (2025)
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Key topics: Drug-Target Interaction, De Novo Drug Design, Drug Repositioning, Toxicity Prediction.
        </Typography>
      </Box>

      {/* Research Area 2: AI-based Healthcare */}
      <Box sx={{ my: 6 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          AI-based Healthcare
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          Our research in AI-based healthcare focuses on developing intelligent systems for disease diagnosis, personalized treatment recommendations, and patient monitoring. We utilize medical imaging analysis, electronic health record data, and predictive analytics to improve patient outcomes.
        </Typography>
        
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 3 }}>
            Featured Publication
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'center' }}>
            <Box sx={{ flex: { md: '0 0 41.6667%' }, width: '100%' }}>
              <img src={featureInjectionImage} alt="Feature Injection Model" style={{ width: '100%', height: 'auto', borderRadius: '8px' }} />
            </Box>
            <Box sx={{ flex: { md: 1 } }}>
              <Typography variant="h6" component="h4" sx={{ fontWeight: 'bold' }}>
                Explainable feature-injected diffusion model for medical image translation
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
                Jung Su Ahn, Ki Hoon Kwak, and Young-Rae Cho
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, fontStyle: 'italic' }}>
                IEEE Access (2025)
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Key topics: Medical Image Analysis, Image-to-Image Translation, Generative Adversarial Networks, Predictive Diagnostics.
        </Typography>
      </Box>
    </Container>
  );
};

export default Research;